import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ShoppingCart, 
  Heart, 
  Star, 
  Filter,
  Search,
  Plus,
  Minus,
  Package,
  Shirt,
  Dumbbell,
  X
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  category: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  featured: boolean;
  description: string;
}

const StorePanel: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cartItems, setCartItems] = useState<{[key: string]: number}>({});
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Items', icon: Package },
    { id: 'apparel', name: 'Apparel', icon: Shirt },
    { id: 'equipment', name: 'Equipment', icon: Dumbbell },
    { id: 'accessories', name: 'Accessories', icon: Heart }
  ];

  const products: Product[] = [
    {
      id: '1',
      name: 'SCULPTD Hooded Tank - "Strength Never Goes Out of Style"',
      price: 39.99,
      originalPrice: 49.99,
      image: '/assets/sculptd-hooded-tank-front.jpg',
      category: 'apparel',
      rating: 4.9,
      reviews: 156,
      inStock: true,
      featured: true,
      description: 'Premium hooded tank with SCULPTD branding - Because Strength Never Goes Out of Style'
    },
    {
      id: '2',
      name: 'SCULPTD Hooded Tank - Back Design',
      price: 39.99,
      originalPrice: 49.99,
      image: '/assets/sculptd-hooded-tank-back.jpg',
      category: 'apparel',
      rating: 4.9,
      reviews: 156,
      inStock: true,
      featured: true,
      description: 'Premium hooded tank with signature back design - Because Strength Never Goes Out of Style'
    },
    {
      id: '3',
      name: 'SCULPTD Premium Yoga Mat',
      price: 34.99,
      originalPrice: 44.99,
      image: '/assets/sculptd-yoga-mat.jpg',
      category: 'equipment',
      rating: 4.7,
      reviews: 156,
      inStock: true,
      featured: false,
      description: 'Non-slip yoga mat with SCULPTD logo - Because Strength Never Goes Out of Style'
    },
    {
      id: '4',
      name: 'SCULPTD Adjustable Dumbbells Set',
      price: 89.99,
      originalPrice: 119.99,
      image: '/assets/sculptd-dumbbells.jpg',
      category: 'equipment',
      rating: 4.6,
      reviews: 78,
      inStock: true,
      featured: true,
      description: 'Space-saving adjustable dumbbells 5-25lbs - Professional grade equipment'
    },
    {
      id: '5',
      name: 'SCULPTD Insulated Water Bottle',
      price: 19.99,
      image: '/assets/sculptd-water-bottle.jpg',
      category: 'accessories',
      rating: 4.5,
      reviews: 203,
      inStock: true,
      featured: false,
      description: 'Insulated stainless steel water bottle with SCULPTD branding'
    },
    {
      id: '6',
      name: 'SCULPTD Speed Jump Rope',
      price: 24.99,
      originalPrice: 29.99,
      image: '/assets/sculptd-jump-rope.jpg',
      category: 'equipment',
      rating: 4.4,
      reviews: 92,
      inStock: true,
      featured: false,
      description: 'Professional speed rope with adjustable length and SCULPTD grip'
    },
    {
      id: '7',
      name: 'SCULPTD Resistance Bands Set',
      price: 39.99,
      image: '/assets/sculptd-resistance-bands.jpg',
      category: 'equipment',
      rating: 4.7,
      reviews: 134,
      inStock: false,
      featured: false,
      description: 'Complete resistance training set with door anchor and SCULPTD branding'
    },
    {
      id: '8',
      name: 'SCULPTD Premium Gym Towel',
      price: 14.99,
      image: '/assets/sculptd-gym-towel.jpg',
      category: 'accessories',
      rating: 4.3,
      reviews: 67,
      inStock: true,
      featured: false,
      description: 'Quick-dry microfiber gym towel with SCULPTD logo'
    }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (productId: string) => {
    setCartItems(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  };

  const removeFromCart = (productId: string) => {
    setCartItems(prev => {
      const newItems = { ...prev };
      if (newItems[productId] > 1) {
        newItems[productId]--;
      } else {
        delete newItems[productId];
      }
      return newItems;
    });
  };

  const toggleFavorite = (productId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(productId)) {
        newFavorites.delete(productId);
      } else {
        newFavorites.add(productId);
      }
      return newFavorites;
    });
  };

  const getTotalCartItems = () => {
    return Object.values(cartItems).reduce((sum, count) => sum + count, 0);
  };

  const getTotalPrice = () => {
    return Object.entries(cartItems).reduce((total, [productId, count]) => {
      const product = products.find(p => p.id === productId);
      return total + (product ? product.price * count : 0);
    }, 0);
  };

  return (
    <div className="space-y-8 sculptd-container-padding">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            SCULPTD Store
          </h1>
          <p className="text-lg mt-2" style={{ 
            color: 'var(--color-text-muted)',
            textShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            Because Strength Never Goes Out of Style
          </p>
        </div>
        <div className="relative">
          <Button 
            onClick={() => setShowCheckout(true)}
            className="sculptd-button-primary sculptd-sheen" 
            style={{ 
              background: 'var(--gradient-cta-primary)',
              boxShadow: 'var(--shadow-lg)',
              minHeight: '44px'
            }}
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Cart ({getTotalCartItems()})
          </Button>
          {getTotalCartItems() > 0 && (
            <Badge 
              className="absolute -top-2 -right-2"
              style={{ 
                background: 'var(--color-accent)', 
                color: 'white',
                minWidth: '20px',
                height: '20px'
              }}
            >
              {getTotalCartItems()}
            </Badge>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="sculptd-card-elevated sculptd-sheen sculptd-card-padding" style={{
        boxShadow: 'var(--shadow-card-separation)'
      }}>
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base w-full rounded-lg border-0"
              style={{ 
                minHeight: '44px',
                background: 'var(--color-background)',
                color: 'var(--color-text)'
              }}
            />
          </div>
          <div className="flex gap-6">
            {/* All Items button */}
            <div className="flex gap-3">
              {categories.filter(cat => cat.id === 'all').map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className="sculptd-sheen px-4 py-2 text-base font-medium rounded-lg flex items-center space-x-2 transition-all duration-300"
                    style={{
                      background: selectedCategory === category.id ? 'var(--gradient-primary)' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'var(--color-text)',
                      minHeight: '44px'
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Apparel column with Equipment and Accessories below */}
            <div className="flex flex-col gap-3">
              {/* Apparel button */}
              {categories.filter(cat => cat.id === 'apparel').map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className="sculptd-sheen px-4 py-2 text-base font-medium rounded-lg flex items-center space-x-2 transition-all duration-300"
                    style={{
                      background: selectedCategory === category.id ? 'var(--gradient-primary)' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'var(--color-text)',
                      minHeight: '44px'
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{category.name}</span>
                  </button>
                );
              })}
              
              {/* Equipment and Accessories buttons below Apparel */}
              {categories.filter(cat => cat.id === 'equipment' || cat.id === 'accessories').map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className="sculptd-sheen px-4 py-2 text-base font-medium rounded-lg flex items-center space-x-2 transition-all duration-300"
                    style={{
                      background: selectedCategory === category.id ? 'var(--gradient-primary)' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'var(--color-text)',
                      minHeight: '44px'
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      {products.some(p => p.featured) && (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Featured Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.filter(p => p.featured).map((product) => (
              <Card key={product.id} className="sculptd-card-elevated sculptd-sheen border-0 hover:shadow-xl transition-all duration-300" style={{
                borderLeft: '4px solid var(--color-primary)',
                boxShadow: 'var(--shadow-card-separation)'
              }}>
                <div className="relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-56 object-cover rounded-t-lg"
                  />
                  {product.originalPrice && (
                    <Badge className="absolute top-3 right-3" style={{ 
                      background: 'var(--color-error)',
                      color: 'white',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0.75rem'
                    }}>
                      Sale
                    </Badge>
                  )}
                  <button
                    onClick={() => toggleFavorite(product.id)}
                    className="absolute top-3 left-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
                  >
                    <Heart 
                      className={`w-5 h-5 ${favorites.has(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
                    />
                  </button>
                </div>
                <CardContent className="sculptd-card-padding">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-xl" style={{ 
                        color: 'var(--color-text)',
                        textShadow: '0 1px 2px rgba(0,0,0,0.08)'
                      }}>
                        {product.name}
                      </h3>
                      <p className="text-base mt-2" style={{ color: 'var(--color-text-muted)' }}>
                        {product.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        ({product.reviews})
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-lg" style={{ color: 'var(--color-primary)' }}>
                          ${product.price}
                        </span>
                        {product.originalPrice && (
                          <span className="text-sm line-through" style={{ color: 'var(--color-text-muted)' }}>
                            ${product.originalPrice}
                          </span>
                        )}
                      </div>
                      <Badge style={{ 
                        background: product.inStock ? 'var(--color-success-light)' : 'var(--color-error-light)',
                        color: product.inStock ? 'var(--color-success)' : 'var(--color-error)'
                      }}>
                        {product.inStock ? 'In Stock' : 'Out of Stock'}
                      </Badge>
                    </div>

                    {cartItems[product.id] ? (
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => removeFromCart(product.id)}
                          className="p-2 rounded-lg sculptd-sheen"
                          style={{ background: 'var(--color-background-secondary)' }}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-medium px-3 text-lg">{cartItems[product.id]}</span>
                        <button
                          onClick={() => addToCart(product.id)}
                          className="p-2 rounded-lg sculptd-sheen"
                          style={{ 
                            background: 'var(--gradient-cta-primary)', 
                            color: 'white',
                            boxShadow: 'var(--shadow-lg)'
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => addToCart(product.id)}
                        disabled={!product.inStock}
                        className="w-full sculptd-button-primary sculptd-sheen text-base font-semibold"
                        style={{ 
                          background: product.inStock ? 'var(--gradient-cta-primary)' : 'var(--color-background-secondary)',
                          color: product.inStock ? 'white' : 'var(--color-text-muted)',
                          boxShadow: product.inStock ? 'var(--shadow-lg)' : 'none',
                          minHeight: '48px'
                        }}
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All Products */}
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            All Products
          </h2>
          <span className="text-base" style={{ color: 'var(--color-text-muted)' }}>
            {filteredProducts.length} products
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="sculptd-card-elevated sculptd-sheen border-0 hover:shadow-xl transition-all duration-300" style={{
              boxShadow: 'var(--shadow-card-separation)'
            }}>
              <div className="relative">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
                {product.originalPrice && (
                  <Badge className="absolute top-3 right-3" style={{ 
                    background: 'var(--color-error)',
                    color: 'white',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem'
                  }}>
                    Sale
                  </Badge>
                )}
                <button
                  onClick={() => toggleFavorite(product.id)}
                  className="absolute top-3 left-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
                >
                  <Heart 
                    className={`w-4 h-4 ${favorites.has(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
                  />
                </button>
              </div>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-base" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {product.name}
                    </h3>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      ({product.reviews})
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-lg" style={{ color: 'var(--color-primary)' }}>
                        ${product.price}
                      </span>
                      {product.originalPrice && (
                        <span className="text-sm line-through" style={{ color: 'var(--color-text-muted)' }}>
                          ${product.originalPrice}
                        </span>
                      )}
                    </div>
                    <Badge style={{ 
                      background: product.inStock ? 'var(--color-success-light)' : 'var(--color-error-light)',
                      color: product.inStock ? 'var(--color-success)' : 'var(--color-error)'
                    }}>
                      {product.inStock ? 'In Stock' : 'Out of Stock'}
                    </Badge>
                  </div>

                  {cartItems[product.id] ? (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => removeFromCart(product.id)}
                        className="p-2 rounded-lg sculptd-sheen"
                        style={{ background: 'var(--color-background-secondary)' }}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-medium px-3">{cartItems[product.id]}</span>
                      <button
                        onClick={() => addToCart(product.id)}
                        className="p-2 rounded-lg sculptd-sheen"
                        style={{ 
                          background: 'var(--gradient-cta-primary)', 
                          color: 'white',
                          boxShadow: 'var(--shadow-lg)'
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => addToCart(product.id)}
                      disabled={!product.inStock}
                      className="w-full sculptd-button-primary sculptd-sheen"
                      style={{ 
                        background: product.inStock ? 'var(--gradient-cta-primary)' : 'var(--color-background-secondary)',
                        color: product.inStock ? 'white' : 'var(--color-text-muted)',
                        boxShadow: product.inStock ? 'var(--shadow-lg)' : 'none',
                        minHeight: '44px'
                      }}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Add to Cart
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Cart Summary */}
      {getTotalCartItems() > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Card className="sculptd-card-elevated sculptd-sheen border-0" style={{
            boxShadow: 'var(--shadow-card-hover)'
          }}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-3">
                  <ShoppingCart className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
                  <span className="font-semibold text-base" style={{ color: 'var(--color-text)' }}>
                    {getTotalCartItems()} items
                  </span>
                </div>
                <div className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  ${getTotalPrice().toFixed(2)}
                </div>
                <Button 
                  className="sculptd-button-primary sculptd-sheen px-6 py-3 text-base font-semibold"
                  style={{ 
                    background: 'var(--gradient-cta-primary)',
                    color: 'white',
                    boxShadow: 'var(--shadow-lg)',
                    minHeight: '44px'
                  }}
                >
                  Checkout
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
                  Checkout
                </h3>
                <button
                  onClick={() => setShowCheckout(false)}
                  className="p-2 rounded-lg transition-all duration-200"
                  style={{
                    background: 'var(--gradient-card-bg)',
                    color: 'var(--color-text-muted)'
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {getTotalCartItems() === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
                  <h4 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
                    Your cart is empty
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Add some items to get started!
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Cart Items */}
                  <div className="space-y-4">
                    {Object.entries(cartItems).map(([productId, quantity]) => {
                      const product = products.find(p => p.id === productId);
                      if (!product) return null;
                      
                      return (
                        <div key={productId} className="flex items-center space-x-4 p-4 rounded-lg" style={{ background: 'var(--gradient-card-bg)' }}>
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h5 className="font-semibold" style={{ color: 'var(--color-text)' }}>
                              {product.name}
                            </h5>
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                              Quantity: {quantity}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold" style={{ color: 'var(--color-primary)' }}>
                              ${(product.price * quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Order Summary */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span style={{ color: 'var(--color-text)' }}>Subtotal:</span>
                      <span style={{ color: 'var(--color-text)' }}>${getTotalPrice().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span style={{ color: 'var(--color-text)' }}>Shipping:</span>
                      <span style={{ color: 'var(--color-text)' }}>Free</span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                      <span style={{ color: 'var(--color-text)' }}>Total:</span>
                      <span style={{ color: 'var(--color-primary)' }}>${getTotalPrice().toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    className="w-full sculptd-button-primary sculptd-sheen"
                    style={{ 
                      background: 'var(--gradient-cta-primary)',
                      color: 'white',
                      boxShadow: 'var(--shadow-lg)',
                      minHeight: '48px'
                    }}
                    onClick={() => {
                      alert('Checkout functionality would be implemented here! (Demo Mode)');
                      setShowCheckout(false);
                      setCartItems({});
                    }}
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Complete Purchase
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePanel;
